import { graphQLClient } from '@api/client';
import {
  useUpdateTreatmentCancelMutation,
  useVideoCallQuery
} from '@api/GoodocAPI';
import { useModalContext } from '@context/Modal';
import useTimer from '@hooks/common/util/useTimer';
import { CANCEL_TREATMENT_BUTTON } from '@pages/Matching/focusKey';
import constate from 'constate';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { MATCHING_FAIL, VIDEO_CALL } from '@router/path';

interface MatchingProps {
  treatmentId: string;
}

const useMatching = (props: MatchingProps) => {
  const { formattedTime } = useTimer({ reverse: { initalTime: 90 } });
  const { treatmentId } = props;
  const navigate = useNavigate();
  const { data: videoCallResponse } = useVideoCallQuery(
    graphQLClient,
    { treatmentId },
    { refetchInterval: 3000 }
  );

  const checkDoctorMatching = useCallback(() => {
    if (videoCallResponse?.opentokConnection.sessionId) {
      navigate(VIDEO_CALL, { state: { treatmentId }, replace: true });
    }
  }, [navigate, treatmentId, videoCallResponse]);

  const { mutateAsync: updateTreatmentCancel } =
    useUpdateTreatmentCancelMutation(graphQLClient);

  const checkMatchingEnd = useCallback(() => {
    if (formattedTime === '00:00') {
      updateTreatmentCancel({ treatmentId }).then(() => {
        navigate(MATCHING_FAIL, { replace: true });
      });
    }
  }, [formattedTime, navigate, treatmentId]);

  const goBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const { openModal, closeModal } = useModalContext();

  const openCancelTreatemtnModal = useCallback(() => {
    openModal({
      title: '진료 요청을 취소하시겠어요?',
      initialFocusKey: CANCEL_TREATMENT_BUTTON,
      exitFocusKey: '',
      buttons: [
        {
          text: '취소',
          focusKey: CANCEL_TREATMENT_BUTTON,
          onClick: async () => {
            await updateTreatmentCancel({ treatmentId });
            closeModal();
            goBack();
          },
          iconType: 'arrow'
        },
        {
          text: '아니오',
          onClick: () => {
            closeModal();
          },
          iconType: 'cancel'
        }
      ]
    });
  }, [closeModal, goBack, openModal, treatmentId, updateTreatmentCancel]);

  return {
    checkDoctorMatching,
    checkMatchingEnd,
    openCancelTreatemtnModal
  };
};

export const [MatchingProvider, useMatchingContext] = constate(useMatching);
